import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, InlineAlert } from '@scramble/ui';
import { useTranslation } from 'react-i18next';
import { useModal } from '@/providers/modal';
import { ModalCenterLayout } from '@/kit/Modal/layouts/center';
import { ModalSwipeLayout } from '@/kit/Modal/layouts/swipe';
import { useWindowSize } from '@/hooks/useWindowSize';
import { MODAL_IDS } from '@/kit/Modal/keys';
import { INCREASED_INCOME_TYPES } from '@/containers/IncreasedIncome';
import { useRoundStatus } from '@/hooks/shared/useRoundStatus';
import { APP_LINKS } from '@/helpers/links';
import { prettyFloatMoney } from '@/helpers/utils';
import { InfoByPeriod } from './info';
import s from './index.module.scss';

const id = MODAL_IDS.increased_income;

export const IncreasedIncomeModal = ({ data }) => {
  const { isActive } = useRoundStatus();
  const { modalIds } = useModal();
  const { t } = useTranslation();

  const modalProps = useMemo(() => {
    const hasTopUpAndNoInvest =
      data.current.total_topup >= data.current.required_total_topup;

    switch (data.status) {
      case INCREASED_INCOME_TYPES.get_increased_income:
        return {
          description: t('affiliate:increased_income_modal.earn_higher_monthly_rate'),
          link: isActive
            ? {
                label: t('affiliate:increased_income_modal.invest'),
                href: APP_LINKS.investing,
              }
            : {
                label: t('affiliate:increased_income_modal.top_up_balance'),
                href: APP_LINKS.cash,
              },
        };
      case INCREASED_INCOME_TYPES.keep_benefiting_done_type_a:
        return {
          description: t(
            'affiliate:increased_income_modal.successfully_met_requirements',
          ),
          bottomDescription: t(
            'affiliate:increased_income_modal.increased_income_accrued',
          ),
        };
      case INCREASED_INCOME_TYPES.keep_benefiting_done_type_b:
        return {
          description: t(
            'affiliate:increased_income_modal.congratulations_active_investment',
            {
              amount: prettyFloatMoney(
                data.current.required_active_investment_balance,
              ),
            },
          ),
          bottomDescription: t(
            'affiliate:increased_income_modal.increased_income_accrued',
          ),
        };
      case INCREASED_INCOME_TYPES.keep_benefiting_done_type_c:
        return {
          description: t('affiliate:increased_income_modal.current_balance_above', {
            amount: prettyFloatMoney(10000),
            required: prettyFloatMoney(
              data.current.required_investments_to_10000,
            ),
          }),
          bottomDescription: t(
            'affiliate:increased_income_modal.increased_income_accrued',
          ),
          link: {
            label: t('affiliate:increased_income_modal.top_up_balance'),
            href: APP_LINKS.cash,
          },
        };
      case INCREASED_INCOME_TYPES.keep_benefiting_save_type_a:
        return {
          description: (
            <div className="f-body-sm">
              {t('affiliate:increased_income_modal.save_increased_income')}
              <ul className={s.list}>
                {!hasTopUpAndNoInvest && (
                  <li>
                    {t('affiliate:increased_income_modal.top_up_account', {
                      amount: prettyFloatMoney(
                        data.current.required_monthly_top_ups,
                      ),
                    })}
                  </li>
                )}
                <li>
                  {t('affiliate:increased_income_modal.invest_additional', {
                    amount: prettyFloatMoney(data.current.required_investments),
                  })}
                </li>
              </ul>
            </div>
          ),
          bottomDescription: t(
            'affiliate:increased_income_modal.increased_income_accrued',
          ),
          link: hasTopUpAndNoInvest
            ? {
                label: t('affiliate:increased_income_modal.invest'),
                href: APP_LINKS.investing,
              }
            : {
                label: t('affiliate:increased_income_modal.top_up_balance'),
                href: APP_LINKS.cash,
              },
        };
      case INCREASED_INCOME_TYPES.keep_benefiting_save_type_b:
        return {
          description: (
            <div className="f-body-sm">
              {t('affiliate:increased_income_modal.get_increased_income')}
              <ul className={s.list}>
                {!hasTopUpAndNoInvest && (
                  <li>
                    {t('affiliate:increased_income_modal.top_up_account', {
                      amount: prettyFloatMoney(
                        data.current.required_monthly_top_ups,
                      ),
                    })}
                  </li>
                )}
                <li>
                  {t('affiliate:increased_income_modal.invest_additional', {
                    amount: prettyFloatMoney(data.current.required_investments),
                  })}
                </li>
              </ul>
            </div>
          ),
          bottomDescription: t(
            'affiliate:increased_income_modal.increased_income_accrued',
          ),
          link: hasTopUpAndNoInvest
            ? {
                label: t('affiliate:increased_income_modal.invest'),
                href: APP_LINKS.investing,
              }
            : {
                label: t('affiliate:increased_income_modal.top_up_balance'),
                href: APP_LINKS.cash,
              },
        };

      default:
        return {};
    }
  }, [data, isActive]);

  return modalIds[id] && data && <Modal data={data} {...modalProps} />;
};

const Modal = ({ data, title, description, bottomDescription, link }) => {
  const { closeModal } = useModal();
  const { isMobile } = useWindowSize();
  const navigate = useNavigate();
  const { t } = useTranslation('affiliate');

  const content = (
    <div className={s.modal}>
      <div className={s.header}>
        <div className={s.title}>
          {title ||
            t('affiliate:increased_income_modal.monthly_income_group_a')}
        </div>
      </div>

      <div className={s.content}>
        <div className={s.content_inner}>
          {typeof description === 'string' ? (
            <div className="f-body-sm">{description}</div>
          ) : (
            description
          )}
          {data && <InfoByPeriod data={data} />}
          {bottomDescription && (
            <InlineAlert type="info" message={bottomDescription} />
          )}
        </div>
      </div>

      <div className={s.footer}>
        {link ? (
          <Button
            label={link.label}
            theme="neutral"
            appearance="filled"
            onClick={() => {
              navigate(link.href);
            }}
          />
        ) : (
          <Button
            label={t('affiliate:increased_income_modal.got_it')}
            theme="neutral"
            onClick={() => closeModal(id)}
            appearance="filled"
            size="lg"
          />
        )}
      </div>
    </div>
  );

  return isMobile ? (
    <ModalSwipeLayout id={id} onClose={() => closeModal(id)} showClose>
      {content}
    </ModalSwipeLayout>
  ) : (
    <ModalCenterLayout id={id} onClose={() => closeModal(id)} showClose>
      {content}
    </ModalCenterLayout>
  );
};
